// src/pages/GroupsPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
  Snackbar,
  Alert,
  Menu,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { request } from '../utils/request';

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [urls, setUrls] = useState([]); // this now contains category docs with category_Name

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    users: [],
    department: '',
    location: '',
    description: '',
    enabled_policies: [],
    enabled_urls: [], // will hold category _id(s)
  });

  // selection state (new)
  const [selectedIds, setSelectedIds] = useState([]);

  // Delete dialog state (single)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk delete dialog state (new)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Snackbar state
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success'); // 'success' | 'error' | 'info' | 'warning'

  // --- Menu (three-dots) state for actions in table rows ---
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const onMenuEdit = (row) => {
    handleMenuClose();
    // reuse existing edit flow
    handleOpen(row);
  };

  const onMenuDelete = (row) => {
    handleMenuClose();
    // reuse existing delete flow
    handleDeleteClick(row);
  };
  // ---------------------------------------------------------//

  const mode = 'light'; // or 'dark'

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [grpData, usrData, polData, urlData] = await Promise.all([
        request('groups', { method: 'get' }),
        request('user', { method: 'get' }),
        request('policies', { method: 'get' }),
        request('urls', { method: 'get' }), // returns category docs: { _id, category_Name, Urls, ... }
      ]);
      setGroups(Array.isArray(grpData) ? grpData : []);
      setUsers(Array.isArray(usrData) ? usrData : []);
      setPolicies(Array.isArray(polData) ? polData : []);
      setUrls(Array.isArray(urlData) ? urlData : []);
    } catch (err) {
      console.error('Failed to fetch data for GroupsPage:', err);
      setGroups([]);
      setUsers([]);
      setPolicies([]);
      setUrls([]);
      showSnack('Failed to load data.', 'error');
    }
  };

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const getNames = (ids, list, key = 'username') =>
    ids?.length
      ? ids
        .map((id) => list.find((item) => item._id === id)?.[key] || '—')
        .join(', ')
      : '—';

  const handleOpen = (grp) => {
    if (grp) {
      setFormData({
        id: grp._id,
        name: grp.name || '',
        users: grp.users || [],
        department: grp.department || '',
        location: grp.location || '',
        description: grp.description || '',
        enabled_policies: grp.enabled_policies || [],
        enabled_urls: grp.enabled_urls || [], // category _ids
      });
      setIsEditing(true);
    } else {
      setFormData({
        id: null,
        name: '',
        users: [],
        department: '',
        location: '',
        description: '',
        enabled_policies: [],
        enabled_urls: [],
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name,
        users: formData.users,
        department: formData.department,
        location: formData.location,
        description: formData.description,
        enabled_policies: formData.enabled_policies,
        enabled_urls: formData.enabled_urls, // send category ids
      };
      const opts = isEditing
        ? { method: 'put', params: formData.id, body: payload }
        : { method: 'post', body: payload };
      await request('groups', opts);
      await fetchAll();
      handleClose();
      showSnack(isEditing ? 'Group updated successfully.' : 'Group created successfully.', 'success');
    } catch (err) {
      console.error('Error saving group:', err);
      showSnack('Failed to save group.', 'error');
    }
  };

  // Delete handlers (single)
  const handleDeleteClick = (group) => {
    setDeletingGroup(group);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingGroup(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingGroup || !deletingGroup._id) return;
    setDeleting(true);
    try {
      await request('groups', { method: 'delete', params: deletingGroup._id });
      // Refresh list
      await fetchAll();
      showSnack(`Group "${deletingGroup.name}" successfully deleted.`, 'success');
      handleCancelDelete();
    } catch (err) {
      console.error('Failed to delete group:', err);
      showSnack('Failed to delete group.', 'error');
      setDeleting(false);
    }
  };

  // Bulk delete handlers (new)
  const handleOpenBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      // delete each selected id (adjust if your API supports batch delete)
      await Promise.all(
        selectedIds.map((id) => request('groups', { method: 'delete', params: id }))
      );
      showSnack(`${selectedIds.length} group(s) deleted.`, 'success');
      setSelectedIds([]);
      await fetchAll();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete failed:', err);
      showSnack('Failed to delete selected groups.', 'error');
      setBulkDeleting(false);
    }
  };

  /**
   * Compute available users to show in the Users multi-select.
   *
   * Rule:
   *  - A user may belong to only one group.
   *  - When creating a group: hide users who are already assigned to any group.
   *  - When editing a group: hide users who are assigned to other groups, but keep users already in the current group selectable.
   */
  const computeAvailableUsers = () => {
    // Build a set of user IDs that are assigned to groups OTHER than the one we're editing (or all groups when creating)
    const assignedUserIds = new Set();
    groups.forEach((g) => {
      // if editing and this is the same group, skip it (we want those users to remain selectable)
      if (formData.id && g._id === formData.id) return;
      if (Array.isArray(g.users)) {
        g.users.forEach((uid) => {
          if (uid) assignedUserIds.add(uid.toString());
        });
      }
    });

    // available = users not present in assignedUserIds
    return users.filter((u) => !assignedUserIds.has((u._id || '').toString()));
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const availableUsers = computeAvailableUsers();

  // --- Selection helpers (new) ---
  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = groups.length > 0 && selectedIds.length === groups.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(groups.map((g) => g._id));
    }
  };

  const handleEditSelected = () => {
    if (selectedIds.length !== 1) return;
    const grp = groups.find((g) => g._id === selectedIds[0]);
    if (grp) {
      handleOpen(grp);
      // optional: keep selection or clear it — we'll clear selection for clarity
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length === 0) return;
    handleOpenBulkDelete();
  };

  return (
    <Box p={2}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <Link
            href="/home"
            underline="hover"
            sx={{
              fontSize: '0.95rem',
              textDecoration: 'none',
              color: mode === 'dark' ? '#fff' : '#2d9cdb',
              '&:hover': {
                color: mode === 'dark' ? '#fff' :'#0b1d51'
              },
            }}
          >
            Dashboard
          </Link>

          <Typography sx={{ fontSize: '0.95rem', color:'black' }}>
            Groups
          </Typography>
        </Breadcrumbs>

        <Button
          size="small"
          variant="contained"
          sx={{ backgroundColor: '#2d9cdb', px:2, py:0.8, '&:hover': { backgroundColor: '#0b1d51', color: '#ffffff ' } }}
          onClick={() => handleOpen()}
        >
          Add Group
        </Button>
      </Stack>

      {/* --- Toolbar that appears when rows are selected (just above the table) --- */}
      {selectedIds.length > 0 && (
        <Box mb={1} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography variant="body2">
            {selectedIds.length} selected
          </Typography>

          <Button
            size="small"
            variant="outlined"
            onClick={handleEditSelected}
            disabled={selectedIds.length !== 1}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleDeleteSelected}
            sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#b71c1c' }, color: '#fff', textTransform: 'none' }}
          >
            Delete
          </Button>

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none' }}
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Groups Table */}
      <Paper elevation={2} sx={{ mb: 3, height: 'calc(100vh - 170px)' }}>
        {/* make the table scroll only (not the whole page) */}
        <Box sx={{ height: '100%', overflow: 'auto', width: '100%' }}>
          <Box
            component="table"
            width="100%"
            sx={{ borderCollapse: 'collapse', minWidth: '100%' }}
          >
            {/* Table head with sticky headers */}
            <Box component="thead">
              <Box component="tr">
                {/* New checkbox header */}
                <Box
                  component="th"
                  p={1}
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#f5f7fb",
                    color: isDark ? '#fff' : '#333',
                    width: 48,
                    textAlign: 'center',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'select all groups' }}
                  />
                </Box>

                {[
                  '#',
                  'Name',
                  'Users',
                  'Department',
                  'Location',
                  'Description',
                  'Policies',
                  'URL Category',
                  'Actions'
                ].map((h, i) => (
                  <Box
                    component="th"
                    key={i}
                    p={1}
                    textAlign={i === 8 ? 'center' : 'left'}
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: "#f5f7fb",
                      color: isDark ? '#fff' : '#333'
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Table body */}
            <Box component="tbody">
              {groups.map((g, i) => (
                <Box component="tr" key={g._id}>
                  {/* row checkbox */}
                  <Box component="td" p={1} width={48} textAlign="center">
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(g._id)}
                      onChange={() => toggleSelect(g._id)}
                      inputProps={{ 'aria-label': `select group ${g.name}` }}
                    />
                  </Box>

                  <Box component="td" p={1}>{i + 1}</Box>
                  <Box component="td" p={1}>{g.name}</Box>
                  <Box component="td" p={1}>{getNames(g.users, users)}</Box>
                  <Box component="td" p={1}>{g.department || '—'}</Box>
                  <Box component="td" p={1}>{g.location || '—'}</Box>
                  <Box component="td" p={1}>{g.description || '—'}</Box>
                  <Box component="td" p={1}>{getNames(g.enabled_policies, policies, 'name')}</Box>
                  <Box component="td" p={1}>{getNames(g.enabled_urls, urls, 'category_Name')}</Box>

                  {/* Actions column: three-dot menu (text items) */}
                  <Box component="td" p={1} textAlign="center">
                    <IconButton
                      size="small"
                      aria-label={`more-actions-${g._id}`}
                      onClick={(e) => handleMenuOpen(e, g._id)}
                      sx={{ p: 0.5 }}
                    >
                      <MoreVertIcon />
                    </IconButton>

                    <Menu
                      id={`row-menu-${g._id}`}
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && menuRowId === g._id}
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      MenuListProps={{ 'aria-labelledby': `more-actions-${g._id}` }}
                    >
                      <MenuItem onClick={() => onMenuEdit(g)}>Edit</MenuItem>
                      <MenuItem onClick={() => onMenuDelete(g)}>Delete</MenuItem>
                    </Menu>
                  </Box>
                </Box>
              ))}

              {groups.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={10} p={3} textAlign="center">
                    No groups found.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Group Form Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ backgroundColor: '#2d9cdb', color: '#fff', py: 1 }}>
          {isEditing ? 'Edit Group' : 'Add Group'}
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={1}>

            {/* 👇 ONLY THIS BOX IS NEW */}
            <Box sx={{ pt: 1 }}>
              <TextField
                size="small"
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
              />
            </Box>



            {/* Users Multi-Select - only show availableUsers */}
            <FormControl size="small" fullWidth>
              <InputLabel>Users</InputLabel>
              <Select
                multiple
                name="users"
                value={formData.users}
                onChange={handleChange}
                input={<OutlinedInput label="Users" />}
                renderValue={(selected) => getNames(selected, users)}
              >
                {availableUsers.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    <Checkbox checked={formData.users.includes(u._id)} />
                    <ListItemText primary={u.username} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                size="small"
                label="Location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                fullWidth
              />
            </Stack>

            <TextField
              size="small"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />

            {/* Policies Multi-Select */}
            <FormControl size="small" fullWidth>
              <InputLabel>Policies</InputLabel>
              <Select
                multiple
                name="enabled_policies"
                value={formData.enabled_policies}
                onChange={handleChange}
                input={<OutlinedInput label="Policies" />}
                renderValue={(selected) => getNames(selected, policies, 'name')}
              >
                {policies.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    <Checkbox checked={formData.enabled_policies.includes(p._id)} />
                    <ListItemText primary={p.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* URLs Multi-Select (now shows categories by category_Name) */}
            <FormControl size="small" fullWidth>
              <InputLabel>URLs</InputLabel>
              <Select
                multiple
                name="enabled_urls"
                value={formData.enabled_urls}
                onChange={handleChange}
                input={<OutlinedInput label="URLs" />}
                renderValue={(selected) => getNames(selected, urls, 'category_Name')}
              >
                {urls.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    <Checkbox checked={formData.enabled_urls.includes(u._id)} />
                    {/* show category_Name instead of parentDomain */}
                    <ListItemText primary={u.category_Name || u.category_name || '—'} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleClose} sx={{ color: '#2d9cdb' }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            sx={{ backgroundColor: '#2d9cdb', '&:hover': { backgroundColor: '#0b1d51', color: '#fff', } }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog (single) */}
      <Dialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Delete Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the group{' '}
            <strong>{deletingGroup ? deletingGroup.name : ''}</strong>?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            sx={{
              backgroundColor: '#e53935',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog (new) */}
      <Dialog
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        aria-labelledby="bulk-delete-dialog-title"
      >
        <DialogTitle id="bulk-delete-dialog-title">Delete Selected Groups</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the following {selectedIds.length} group(s)?
          </Typography>
          <Box sx={{ mt: 1 }}>
            {selectedIds.map((id) => {
              const grp = groups.find((g) => g._id === id);
              return (
                <Typography key={id} variant="body2">
                  • {grp ? grp.name : id}
                </Typography>
              );
            })}
          </Box>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button size="small" onClick={handleCancelBulkDelete} disabled={bulkDeleting}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleConfirmBulkDelete}
            disabled={bulkDeleting}
            sx={{
              backgroundColor: '#e53935',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            {bulkDeleting ? 'Deleting…' : `Yes, Delete (${selectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
